/*
 * Clinic user session (login at /clinic/:name/login).
 *
 * localStorage is shared by every tab and survives a reload; sessionStorage is per tab.
 * The token, ids and role live in localStorage; the permission flags the screens read from
 * sessionStorage are mirrored to localStorage too, so a tab opened later (Ctrl+click, new window)
 * gets them back before anything renders (restoreTabSession, called from index.js).
 */

// what a clinic login writes to localStorage (cleared on logout)
const SESSION_KEYS = ["token", "userRole", "names", "userId", "hospitalId", "sessionClinic"];
// per-screen leftovers that must not leak to the next user of the same browser
const LEFTOVER_KEYS = ["PatientID", "PatientUUID", "sesInvoice", "previousState", "oocyteMainTab"];
// sessionStorage flags written at login (UserValidate) and read directly by the screens
const TAB_KEYS = ["pei", "createDonor", "viewDonor", "addDonor", "pvp", "pcp"];
const MIRROR_PREFIX = "hbTab:";

// a login whose user still has to accept the Terms & Conditions: kept in memory only, so a reload
// (or closing the tab) before accepting leaves no usable session behind
let pendingSession = null;

/** Save the login, or hold it until the Terms & Conditions are accepted (commitPendingSession). */
export const saveSession = (items, { pending = false } = {}) => {
	if (pending) {
		pendingSession = { ...items };
		return;
	}
	pendingSession = null;
	Object.entries(items).forEach(([key, value]) => localStorage.setItem(key, value));
};

export const commitPendingSession = () => {
	if (pendingSession) saveSession(pendingSession);
};

/** A session value, looking at a login waiting for T&C acceptance first. Raw string, like localStorage. */
export const getSessionItem = (key) =>
	pendingSession && key in pendingSession ? pendingSession[key] : localStorage.getItem(key);

/** sessionStorage.setItem for the login flags, mirrored so later tabs get them (see restoreTabSession). */
export const setTabItem = (key, value) => {
	sessionStorage.setItem(key, value);
	localStorage.setItem(MIRROR_PREFIX + key, value);
};

/** New tab of a signed-in user: copy the mirrored login flags back into this tab's sessionStorage. */
export const restoreTabSession = () => {
	try {
		if (!localStorage.getItem("token")) return;
		TAB_KEYS.forEach((key) => {
			const value = localStorage.getItem(MIRROR_PREFIX + key);
			if (value !== null && sessionStorage.getItem(key) === null) sessionStorage.setItem(key, value);
		});
	} catch (e) {
		// storage blocked: the screens fall back to their no-permission state, as before
	}
};

/** The clinic URL the stored session belongs to; null for sessions saved before this was recorded. */
export const getSessionClinic = () => localStorage.getItem("sessionClinic");

const removeSessionKeys = () =>
	[...SESSION_KEYS, ...LEFTOVER_KEYS, ...TAB_KEYS.map((k) => MIRROR_PREFIX + k)].forEach((key) =>
		localStorage.removeItem(key)
	);

export const clearClinicSession = () => {
	pendingSession = null;
	removeSessionKeys();
	sessionStorage.clear();
};

/**
 * Login page without a token: remove what a screen may have written back while the previous
 * session was being torn down (localStorage only; this tab's sessionStorage is the login page's).
 */
export const clearStaleSession = () => {
	if (localStorage.getItem("token")) return;
	removeSessionKeys();
};

// clinic URL of the current page (/clinic/<name>/...), if any
export const clinicFromPath = (pathname = window.location.pathname) => {
	const match = pathname.match(/^\/clinic\/([^/]+)/);
	return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Sign out and go to the clinic login with a full page load, so nothing of the previous user
 * (Redux store, React Query cache, component state) survives into the next login.
 * `reason` is shown once on the login page (see takeLogoutReason).
 */
export const logoutToLogin = (clinic, reason) => {
	const target = clinic || clinicFromPath() || getSessionClinic();
	clearClinicSession();
	if (reason) sessionStorage.setItem("logoutReason", reason);
	window.location.replace(target ? `/clinic/${encodeURIComponent(target)}/login` : "/");
};

// Global Admin / Super Admin logins (GlobalLogin at "/"), each with its own keys
const ADMIN_KEYS = {
	global: ["globalAdminToken", "GlobalroleForAddClinic", "GlobaluserEmail", "GlobaluserId"],
	super: ["SuperAdminToken", "SuperroleForAddClinic", "SuserEmail", "SuserId"],
};

/** Sign the Global Admin or Super Admin out and reload into the admin login, like logoutToLogin. */
export const logoutAdmin = (kind) => {
	pendingSession = null;
	(ADMIN_KEYS[kind] || []).forEach((key) => localStorage.removeItem(key));
	sessionStorage.clear();
	window.location.replace("/");
};

export const takeLogoutReason = () => {
	const reason = sessionStorage.getItem("logoutReason");
	if (reason) sessionStorage.removeItem("logoutReason");
	return reason;
};
